import React from "react";
import { Helmet } from "react-helmet-async";

export interface Hreflang { lang: string; url: string; }
export interface DublinCoreProps {
    title?: string;
    creator?: string;
    subject?: string;
    description?: string;
    publisher?: string;
    date?: string;
    type?: string;
    language?: string;
    rights?: string;
}

export interface SeoHeadProps {
    // Core
    title: string;
    description: string;
    keywords?: string;
    canonical?: string;
    author?: string;
    robots?: string;

    // Open Graph
    ogType?: string;
    ogUrl?: string;
    ogSiteName?: string;
    ogImage?: string;
    ogImageAlt?: string;
    ogLocale?: string;
    ogLocaleAlternates?: string[];

    // Twitter
    twitterCard?: "summary" | "summary_large_image" | "app" | "player";
    twitterSite?: string;
    twitterCreator?: string;
    twitterImage?: string;
    twitterImageAlt?: string;
    twitterLabel1?: string;
    twitterData1?: string;
    twitterLabel2?: string;
    twitterData2?: string;

    // AI bots — single switch, defaults true so every existing page picks up AI signals
    aiBots?: boolean;

    // Geo / local
    geoRegion?: string;
    geoPlacename?: string;
    geoPosition?: string; // "lat;long"

    // Dublin Core
    dublinCore?: DublinCoreProps;

    // International SEO
    hreflangs?: Hreflang[];

    // Product OG
    productBrand?: string;
    productAvailability?: string;
    productCondition?: string;
    productPriceAmount?: string;
    productPriceCurrency?: string;
    productMaterial?: string;
    productColor?: string;
    productSize?: string;
    productCategory?: string;

    // Verification tokens
    googleVerification?: string;
    bingVerification?: string;
    yandexVerification?: string;
    pinterestVerification?: string;
    facebookDomainVerification?: string;

    // JSON-LD schemas (each rendered as its own <script type="application/ld+json">)
    jsonLd?: object[];

    // Theme color
    themeColor?: string;
}

// Standard AI / LLM crawler list — kept in sync with SEO/generate_artecraft_seo_pages.py
const AI_BOTS: ReadonlyArray<string> = [
    "GPTBot",
    "ChatGPT-User",
    "OAI-SearchBot",
    "ClaudeBot",
    "Claude-Web",
    "anthropic-ai",
    "PerplexityBot",
    "Perplexity-User",
    "Google-Extended",
    "Bingbot-AI",
    "Bytespider",
    "CCBot",
    "FacebookBot",
    "Meta-ExternalAgent",
    "Meta-ExternalFetcher",
    "Applebot",
    "Applebot-Extended",
    "DuckAssistBot",
    "cohere-ai",
    "YouBot",
    "MistralAI-User",
    "AI2Bot",
    "amazonbot",
];

const SeoHead: React.FC<SeoHeadProps> = ({
    title,
    description,
    keywords,
    canonical,
    author,
    robots,
    ogType,
    ogUrl,
    ogSiteName,
    ogImage,
    ogImageAlt,
    ogLocale,
    ogLocaleAlternates,
    twitterCard,
    twitterSite,
    twitterCreator,
    twitterImage,
    twitterImageAlt,
    twitterLabel1,
    twitterData1,
    twitterLabel2,
    twitterData2,
    aiBots = true,
    geoRegion,
    geoPlacename,
    geoPosition,
    dublinCore,
    hreflangs,
    productBrand,
    productAvailability,
    productCondition,
    productPriceAmount,
    productPriceCurrency,
    productMaterial,
    productColor,
    productSize,
    productCategory,
    googleVerification,
    bingVerification,
    yandexVerification,
    pinterestVerification,
    facebookDomainVerification,
    jsonLd,
    themeColor,
}) => {
    return (
        <Helmet>
            {/* Core */}
            <title>{title}</title>
            <meta name="title" content={title} />
            <meta name="description" content={description} />
            {keywords && <meta name="keywords" content={keywords} />}
            {author && <meta name="author" content={author} />}
            {robots && <meta name="robots" content={robots} />}
            {robots && <meta name="googlebot" content={robots} />}
            {robots && <meta name="bingbot" content={robots} />}
            {canonical && <link rel="canonical" href={canonical} />}

            {/* AI / LLM crawlers */}
            {aiBots &&
                AI_BOTS.map((bot) => (
                    <meta key={bot} name={bot} content="index, follow" />
                ))}
            {aiBots && <meta name="ai-content-declaration" content="human-authored, expert-reviewed" />}
            {aiBots && <meta name="ai-training-allowed" content="yes" />}
            {aiBots && <meta name="ai-summarization-allowed" content="yes" />}
            {aiBots && <meta name="ai-citation-preferred-name" content="Arte Craft India" />}
            {aiBots && <meta name="ai-content-quality" content="expert, original, fact-checked" />}
            {aiBots && <meta name="content-source" content="primary-manufacturer" />}

            {/* Geo / local */}
            {geoRegion && <meta name="geo.region" content={geoRegion} />}
            {geoPlacename && <meta name="geo.placename" content={geoPlacename} />}
            {geoPosition && <meta name="geo.position" content={geoPosition} />}
            {geoPosition && <meta name="ICBM" content={geoPosition.replace(";", ", ")} />}

            {/* Dublin Core */}
            {dublinCore?.title && <meta name="DC.title" content={dublinCore.title} />}
            {dublinCore?.creator && <meta name="DC.creator" content={dublinCore.creator} />}
            {dublinCore?.subject && <meta name="DC.subject" content={dublinCore.subject} />}
            {dublinCore?.description && <meta name="DC.description" content={dublinCore.description} />}
            {dublinCore?.publisher && <meta name="DC.publisher" content={dublinCore.publisher} />}
            {dublinCore?.date && <meta name="DC.date" content={dublinCore.date} />}
            {dublinCore?.type && <meta name="DC.type" content={dublinCore.type} />}
            {dublinCore?.language && <meta name="DC.language" content={dublinCore.language} />}
            {dublinCore?.rights && <meta name="DC.rights" content={dublinCore.rights} />}

            {/* Hreflang */}
            {hreflangs &&
                hreflangs.map((h) => (
                    <link key={h.lang} rel="alternate" hrefLang={h.lang} href={h.url} />
                ))}

            {/* Open Graph */}
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            {ogType && <meta property="og:type" content={ogType} />}
            {ogUrl && <meta property="og:url" content={ogUrl} />}
            {ogSiteName && <meta property="og:site_name" content={ogSiteName} />}
            {ogImage && <meta property="og:image" content={ogImage} />}
            {ogImage && <meta property="og:image:secure_url" content={ogImage} />}
            {ogImage && <meta property="og:image:width" content="1200" />}
            {ogImage && <meta property="og:image:height" content="630" />}
            {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}
            {ogLocale && <meta property="og:locale" content={ogLocale} />}
            {ogLocaleAlternates &&
                ogLocaleAlternates.map((alt) => (
                    <meta key={alt} property="og:locale:alternate" content={alt} />
                ))}

            {/* Product OG */}
            {productBrand && <meta property="product:brand" content={productBrand} />}
            {productAvailability && <meta property="product:availability" content={productAvailability} />}
            {productCondition && <meta property="product:condition" content={productCondition} />}
            {productPriceAmount && <meta property="product:price:amount" content={productPriceAmount} />}
            {productPriceCurrency && <meta property="product:price:currency" content={productPriceCurrency} />}
            {productMaterial && <meta property="product:material" content={productMaterial} />}
            {productColor && <meta property="product:color" content={productColor} />}
            {productSize && <meta property="product:size" content={productSize} />}
            {productCategory && <meta property="product:category" content={productCategory} />}

            {/* Twitter / X Card */}
            {twitterCard && <meta name="twitter:card" content={twitterCard} />}
            {twitterCard && <meta name="twitter:title" content={title} />}
            {twitterCard && <meta name="twitter:description" content={description} />}
            {twitterSite && <meta name="twitter:site" content={twitterSite} />}
            {twitterCreator && <meta name="twitter:creator" content={twitterCreator} />}
            {twitterImage && <meta name="twitter:image" content={twitterImage} />}
            {twitterImageAlt && <meta name="twitter:image:alt" content={twitterImageAlt} />}
            {twitterLabel1 && <meta name="twitter:label1" content={twitterLabel1} />}
            {twitterData1 && <meta name="twitter:data1" content={twitterData1} />}
            {twitterLabel2 && <meta name="twitter:label2" content={twitterLabel2} />}
            {twitterData2 && <meta name="twitter:data2" content={twitterData2} />}

            {/* Verification */}
            {googleVerification && <meta name="google-site-verification" content={googleVerification} />}
            {bingVerification && <meta name="msvalidate.01" content={bingVerification} />}
            {yandexVerification && <meta name="yandex-verification" content={yandexVerification} />}
            {pinterestVerification && <meta name="p:domain_verify" content={pinterestVerification} />}
            {facebookDomainVerification && (
                <meta name="facebook-domain-verification" content={facebookDomainVerification} />
            )}

            {/* Theme color */}
            {themeColor && <meta name="theme-color" content={themeColor} />}

            {/* JSON-LD schemas */}
            {jsonLd &&
                jsonLd.map((schema, i) => (
                    <script key={i} type="application/ld+json">
                        {JSON.stringify(schema)}
                    </script>
                ))}
        </Helmet>
    );
};

export default SeoHead;
